import { useMemo, useState } from 'react';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { faBolt, faPlus, faSplit } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConflictingTriggersAlert } from 'assets/js/pages/engage/workflows/components/ConflictingTriggersPanel';
import {
    NoAuctionsAlert,
    noAuctionsAlertTriggers,
} from 'assets/js/pages/engage/workflows/components/NoAuctionsPanel';
import { useCampaignSelectItems } from 'assets/js/pages/engage/workflows/hooks/useCampaignSelectItems';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import {
    actionsHaveFormDataSelector,
    nodeFormErrorsSelector,
    useReactFlowStore,
    workflowHasActionSelector,
} from 'assets/js/pages/engage/workflows/utils/store';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import {
    WorkflowActionTypeEnum,
    WorkflowTriggerTypeEnum,
    WorkflowUtilityNodeTypeEnum,
} from 'constants/workflows';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { twMerge } from 'tailwind-merge';
import { useShallow } from 'zustand/react/shallow';

const BuilderAlerts = () => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const { data: workflow } = useCurrentWorkflow();

    const { data: auctions } = useCampaignSelectItems('auction-campaigns');

    const { errors, errorCount } = useReactFlowStore(useShallow(nodeFormErrorsSelector));
    const { getNodes, setNodes, setShouldLayout } = useReactFlowStore(
        useShallow(({ setNodes, getNodes, setShouldLayout }) => ({
            setNodes,
            getNodes,
            setShouldLayout,
        })),
    );
    const hasNodes = getNodes().length > 0;
    const workflowHasAction = useReactFlowStore(workflowHasActionSelector);
    const actionsHaveFormData = useReactFlowStore(actionsHaveFormDataSelector);
    const isAnyNodeSelected = useReactFlowStore(state => state.nodes.some(n => n.selected));
    const noAuctionError =
        auctions.length <= 0 && noAuctionsAlertTriggers.includes(workflow.trigger);
    const formErrorCount =
        errorCount +
        ((!workflowHasAction || !actionsHaveFormData) && !isAnyNodeSelected && hasNodes ? 1 : 0);
    const otherErrorCount = useMemo(() => {
        let count = 0;

        if (workflow.has_conflicting_triggers) count++;
        if (noAuctionError) count++;
        return count;
    }, [noAuctionError, workflow]);
    const totalErrorCount = formErrorCount + otherErrorCount;

    return (
        <Popover.Root open={popoverVisible} onOpenChange={isOpen => setPopoverVisible(isOpen)}>
            <Popover.Trigger asChild>
                <Button variant="outline" color="neutral">
                    Alerts
                    <Badge
                        size="sm"
                        className={twMerge(
                            'h-5 bg-gray-400 text-white',
                            totalErrorCount > 0 && 'bg-red-500',
                        )}
                    >
                        {totalErrorCount}
                    </Badge>
                </Button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content side="bottom" align="end" sideOffset={8} className="min-w-96">
                    <div className="border-b border-b-gray-200 p-4 text-sm font-medium">
                        All alerts
                    </div>

                    {workflow.has_conflicting_triggers && (
                        <div className="p-4">
                            <Box className="p-4 pr-8">
                                <ConflictingTriggersAlert />
                            </Box>
                        </div>
                    )}

                    {noAuctionError && (
                        <div className="p-4">
                            <Box className="p-4 pr-8">
                                <NoAuctionsAlert />
                            </Box>
                        </div>
                    )}

                    {formErrorCount > 0 && (
                        <div className="flex flex-col gap-2">
                            <ul className="flex flex-col">
                                {!workflowHasAction && !isAnyNodeSelected && (
                                    <li className="border-b border-gray-200 p-2 last:border-b-0">
                                        <div className="hover:bg-gray-25 flex w-full max-w-96 flex-col gap-1.5 rounded-lg p-3 px-3.5">
                                            <div className="flex items-center gap-1.5">
                                                <FontAwesomeIcon icon={faPlus} />
                                                <p className="text-sm font-medium">Add step</p>
                                            </div>

                                            <p className="ml-5 text-sm text-red-600">
                                                You need at least one action in order to publish
                                                your workflow
                                            </p>
                                        </div>
                                    </li>
                                )}

                                {!actionsHaveFormData && !isAnyNodeSelected && (
                                    <li className="border-b border-gray-200 p-2 last:border-b-0">
                                        <div className="hover:bg-gray-25 flex w-full max-w-96 flex-col gap-1.5 rounded-lg p-3 px-3.5">
                                            <div className="flex items-center gap-1.5">
                                                <FontAwesomeIcon icon={faPlus} />
                                                <p className="text-sm font-medium">Add step</p>
                                            </div>
                                            <p className="ml-5 text-sm text-red-600">
                                                All actions must be configured to publish your
                                                workflow.
                                            </p>
                                        </div>
                                    </li>
                                )}

                                {errors.map(errorNode => (
                                    <BuilderAlertItem
                                        errorNode={errorNode}
                                        key={errorNode.id}
                                        onClick={() => {
                                            const newNodes = getNodes().map(n => {
                                                if (n.id === errorNode.id) {
                                                    return {
                                                        ...n,
                                                        selected: true,
                                                    };
                                                } else {
                                                    return {
                                                        ...n,
                                                        selected: false,
                                                    };
                                                }
                                            });

                                            setNodes(newNodes, { setEdges: false });
                                            setShouldLayout(true);
                                            setPopoverVisible(false);
                                        }}
                                    />
                                ))}
                            </ul>
                        </div>
                    )}

                    {!formErrorCount && !otherErrorCount && (
                        <ul>
                            <li className="py-10">
                                <p className="text-center text-sm font-medium text-gray-500">
                                    No issues with your workflow 💪
                                </p>
                            </li>
                        </ul>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

interface BuilderAlertItemProps {
    errorNode: NodeWithData;
    onClick: () => void;
}
const BuilderAlertItem = ({ errorNode, onClick }: BuilderAlertItemProps) => {
    const formErrors = errorNode.data?.formData?.errors;

    const header = useMemo(() => {
        if (!errorNode.type) return null;

        if (
            Object.values(WorkflowActionTypeEnum).includes(errorNode.type as WorkflowActionTypeEnum)
        ) {
            return getActionNodeHeaderDetails(errorNode.type as WorkflowActionTypeEnum);
        }

        if (
            Object.values(WorkflowTriggerTypeEnum).includes(
                errorNode.type as WorkflowTriggerTypeEnum,
            )
        ) {
            const trigger = enabledTriggers.find(t => t.slug === errorNode.type);
            if (!trigger) return null;
            return {
                icon: faBolt,
                text: trigger.name,
            };
        }

        if (errorNode.type === WorkflowUtilityNodeTypeEnum.BRANCH) {
            return {
                icon: faSplit,
                text: 'Branch',
            };
        }

        return null;
    }, [errorNode.type]);

    const errorMessages = useMemo(() => {
        if (!formErrors) return [];
        let messages: string[] = [];

        const findErrorMessages = (errors: Record<string, any>) => {
            for (const key in errors) {
                if (errors[key]?.message && typeof errors[key].message === 'string') {
                    messages.push(errors[key].message);
                } else {
                    findErrorMessages(errors[key]);
                }
            }
        };

        findErrorMessages(formErrors);
        return messages;
    }, [formErrors]);

    if (!formErrors || !header) return null;

    return (
        <li key={errorNode.id} className="border-b border-gray-200 p-2 last:border-b-0">
            <Button
                variant="none"
                color="neutral"
                onClick={onClick}
                size="sm"
                className="w-full flex-col items-start p-3 hover:bg-gray-50"
            >
                <div className="flex items-center gap-1.5">
                    <FontAwesomeIcon
                        icon={header.icon}
                        className={twMerge(
                            'h-4 w-4 text-green-500',
                            errorNode.type === WorkflowUtilityNodeTypeEnum.BRANCH && 'rotate-90',
                        )}
                    />
                    <p className="font-medium text-gray-900">{header.text}</p>
                </div>

                <div className="flex flex-col">
                    {errorMessages?.map(message => (
                        <p className="ml-5 items-start text-red-600" key={message}>
                            {message}
                        </p>
                    ))}
                </div>
            </Button>
        </li>
    );
};

export { BuilderAlerts };
